import React, { useCallback } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import CloseIcon from '@atlaskit/icon/core/close';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { Box, Inline, xcss, Text } from '@atlaskit/primitives';
import Lozenge from '@atlaskit/lozenge';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getProjectName, projectIdSelector } from '../../../state/selectors/app/index.tsx';
import {
	CTA_SOURCE_COMPONENT,
	CTA_SOURCE_CONTEXT,
	USER_PROPERTY_KEY_NAMES,
	type Flow,
	ELEMENT_IDS,
} from '../constants.tsx';
import { useIsBacklogCardListSizeGreaterThanTwo } from '../utils.tsx';
import messages from '../messages.tsx';
import { onClickCTA } from '../on-click-cta/utils.tsx';

import { AdControls } from './ad-controls/view.tsx';
import { useAdControls } from './ad-controls/controller.tsx';
import { Arrow } from './arrow.tsx';

type BannerProps = {
	isCreateSprintButtonVisible: boolean;
	flow: Flow;
	hoverPulse: boolean;
	setHoverPulse: (hoverPulse: boolean) => void;
};

export const Banner = ({
	isCreateSprintButtonVisible,
	flow,
	hoverPulse,
	setHoverPulse,
}: BannerProps) => {
	const crossFlowEligible = flow === 'crossFlow';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const crossflow = useCrossFlow();
	const threeIssues = useIsBacklogCardListSizeGreaterThanTwo();
	const { hasDismissed, hasDismissedBanner, dismiss } = useAdControls();

	const projectName = useBacklogSelector(getProjectName);
	const projectId = useBacklogSelector(projectIdSelector);

	const onClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'planOnWhiteboardBanner',
			{
				experiment: 'backlogButtonRefresh',
			},
		);

		onClickCTA({
			createAnalyticsEvent,
			crossFlowEligible,
			crossflow,
			projectId,
			projectName,
			component: CTA_SOURCE_COMPONENT,
			context: CTA_SOURCE_CONTEXT,
			coUseEligible: false,
			url: '',
		});
	}, [crossFlowEligible, projectId, projectName, crossflow, createAnalyticsEvent]);

	const onMouseEnter = useCallback(() => {
		setHoverPulse(true);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'hovered',
				actionSubject: 'banner',
			}),
			'planOnWhiteboardBanner',
			{
				experiment: 'backlogButtonRefresh',
			},
		);
	}, [setHoverPulse, createAnalyticsEvent]);

	const onMouseLeave = useCallback(() => {
		setHoverPulse(false);
	}, [setHoverPulse]);

	const handleDismissEntrypoint = useCallback(async () => {
		await dismiss(USER_PROPERTY_KEY_NAMES.WHITEBOARDS_BACKLOG_ENTRYPOINT_DISMISSED);
	}, [dismiss]);

	const handleDismissBanner = useCallback(async () => {
		await dismiss(USER_PROPERTY_KEY_NAMES.BANNER_DISMISSED);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'dismissWhiteboardBanner',
			{
				experiment: 'backlogButtonRefresh',
			},
		);
	}, [dismiss, createAnalyticsEvent]);

	if (!crossFlowEligible || !threeIssues || hasDismissed || hasDismissedBanner) return null;

	return (
		<>
			<Box xcss={outterContainer} id={ELEMENT_IDS.BANNER}>
				<Box
					onClick={onClick}
					xcss={container}
					testId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.banner"
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				>
					<Inline space="space.150" alignBlock="center" xcss={content}>
						<Box xcss={icon}>
							<WhiteboardIcon
								label={formatMessage(messages.bannerIcon)}
								color={token('color.icon.accent.purple')}
							/>
						</Box>
						<Text>{formatMessage(messages.banner)}</Text>
						<Lozenge appearance="new">{formatMessage(messages.planOnWhiteboardTry)}</Lozenge>
					</Inline>
				</Box>
				<Inline space="space.100" alignBlock="center" xcss={buttons}>
					<AdControls isCta={false} onDismissAd={handleDismissEntrypoint} />
					<IconButton
						onClick={handleDismissBanner}
						icon={CloseIcon}
						label={formatMessage(messages.bannerDismiss)}
						appearance="subtle"
					/>
				</Inline>
				{hoverPulse && (
					<Arrow
						fromElementId={ELEMENT_IDS.BANNER}
						toElementId={ELEMENT_IDS.TOUCHPOINT}
						fromCenter={!isCreateSprintButtonVisible}
						color={token('color.text')}
					/>
				)}
			</Box>
			<FireScreenAnalytics attributes={{ experiment: 'backlogButtonRefresh' }} />
		</>
	);
};

const outterContainer = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	paddingLeft: 'space.075',
	paddingRight: 'space.150',
	height: '40px',
	width: '100%',
	position: 'relative',
	borderTop: `1px solid ${token('color.border')}`,
	borderRight: `1px solid ${token('color.border')}`,
	borderLeft: `1px solid ${token('color.border')}`,
	backgroundColor: 'color.background.input.pressed',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const container = xcss({
	display: 'flex',
	paddingLeft: 'space.150',
	paddingRight: 'space.050',
	alignItems: 'center',
	flex: '1 0 0',
	alignSelf: 'stretch',
	':hover': {
		cursor: 'pointer',
	},
});

const icon = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '24px',
	width: '24px',
	backgroundColor: 'color.background.accent.purple.subtlest',
	borderRadius: '4px',
});

const content = xcss({
	display: 'flex',
	flex: '1 0 0',
});

const buttons = xcss({
	display: 'flex',
});
